import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const now = new Date();

const TYPECLS_CLASS = {
  정규: 'typecls-regular',
  특별: 'typecls-special',
  보충: 'typecls-supplement',
  방과후: 'typecls-afterschool',
  행사: 'typecls-event',
  휴업: 'typecls-closure',
  기타: 'typecls-etc',
};

function typeclsClassName(typecls) {
  return TYPECLS_CLASS[typecls] || 'typecls-etc';
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function toDateStr(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

const TODAY_STR = toDateStr(now.getFullYear(), now.getMonth() + 1, now.getDate());

// 화면에 나란히 보여줄 두 달 (year, month) ~ (year, month+1) 을 계산
function nextMonth(year, month) {
  return month === 12 ? { year: year + 1, month: 1 } : { year, month: month + 1 };
}

function rangeForMonths(first, second) {
  const lastDay = new Date(second.year, second.month, 0).getDate();
  return {
    start: toDateStr(first.year, first.month, 1),
    end: toDateStr(second.year, second.month, lastDay),
  };
}

// mobile: 스마트폰 앱 화면. 학사일정은 기본으로 제외하고, 오늘 날짜를 선택된 상태(파란 굵은 테두리)로 시작하며,
// 일정 팝업은 날짜를 눌렀을 때만 뜬다.
export default function TeacherSchedule({ mobile = false }) {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [grade, setGrade] = useState(mobile ? 0 : 1);
  const [academicByDate, setAcademicByDate] = useState({});
  const [myByDate, setMyByDate] = useState({});
  const [activeDate, setActiveDate] = useState(null);
  const [selectedDate, setSelectedDate] = useState(mobile ? TODAY_STR : null);
  const [editingId, setEditingId] = useState(null);
  const [titleInput, setTitleInput] = useState('');

  const second = useMemo(() => nextMonth(year, month), [year, month]);

  const load = useCallback(() => {
    const { start, end } = rangeForMonths({ year, month }, second);
    if (grade === 0) {
      setAcademicByDate({});
    } else {
      api.get('/admin/academic-schedule', { params: { start, end, grade } }).then((res) => {
        const grouped = {};
        res.data.forEach((e) => {
          grouped[e.schedate] = grouped[e.schedate] || [];
          grouped[e.schedate].push(e);
        });
        setAcademicByDate(grouped);
      });
    }
    api.get('/teacher/schedule', { params: { start, end } }).then((res) => {
      const grouped = {};
      res.data.forEach((e) => {
        grouped[e.schedate] = grouped[e.schedate] || [];
        grouped[e.schedate].push(e);
      });
      setMyByDate(grouped);
    });
  }, [year, month, second, grade]);

  useEffect(() => {
    load();
  }, [load]);

  const goPrevMonth = () => {
    if (month === 1) {
      setYear((y) => y - 1);
      setMonth(12);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (month === 12) {
      setYear((y) => y + 1);
      setMonth(1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const goToday = () => {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
    if (mobile) setSelectedDate(TODAY_STR);
  };

  const resetForm = () => {
    setEditingId(null);
    setTitleInput('');
  };

  const openDay = (dateStr) => {
    if (mobile) setSelectedDate(dateStr);
    setActiveDate(dateStr);
    resetForm();
  };

  const closeDay = () => {
    setActiveDate(null);
    resetForm();
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setTitleInput(entry.title);
  };

  const handleSave = async () => {
    const title = titleInput.trim();
    if (!title) return;
    if (editingId) {
      await api.put(`/teacher/schedule/${editingId}`, { schedate: activeDate, title });
    } else {
      await api.post('/teacher/schedule', { schedate: activeDate, title });
    }
    resetForm();
    await load();
  };

  const handleDelete = async (id) => {
    await api.delete(`/teacher/schedule/${id}`);
    if (editingId === id) resetForm();
    await load();
  };

  const activeAcademic = activeDate ? academicByDate[activeDate] || [] : [];
  const activeMine = activeDate ? myByDate[activeDate] || [] : [];

  return (
    <div className="page">
      <div className="page-title-row">
        <button className="btn-ghost" type="button" onClick={goPrevMonth}>◀</button>
        <h2>{month}월 일정</h2>
        <button className="btn-ghost" type="button" onClick={goNextMonth}>▶</button>
        <button className="btn-ghost" type="button" onClick={goToday}>오늘</button>
        <label>
          <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
            <option value={1}>1학년 학사일정</option>
            <option value={2}>2학년 학사일정</option>
            <option value={3}>3학년 학사일정</option>
            <option value={0}>학사일정 제외</option>
          </select>
        </label>
      </div>

      <div className="card">
        <div className="teacher-schedule-months">
          <MonthGrid
            year={year}
            month={month}
            academicByDate={academicByDate}
            myByDate={myByDate}
            selectedDate={selectedDate}
            onDayClick={openDay}
          />
          <MonthGrid
            year={second.year}
            month={second.month}
            academicByDate={academicByDate}
            myByDate={myByDate}
            selectedDate={selectedDate}
            onDayClick={openDay}
          />
        </div>
      </div>

      {activeDate && (
        <div className="modal-overlay" onClick={closeDay}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{activeDate} 일정</h3>
              <button className="btn-ghost" type="button" onClick={closeDay}>닫기</button>
            </div>

            {grade !== 0 && (
              <>
                <h4 className="teacher-schedule-section-label">{grade}학년 학사일정</h4>
                {activeAcademic.length === 0 && (
                  <div className="empty-row">등록된 학사일정이 없습니다.</div>
                )}
                {activeAcademic.map((e) => (
                  <div key={e.id} className="academic-calendar-entry-row">
                    <span>
                      <span className={`academic-calendar-typecls-badge ${typeclsClassName(e.typecls)}`}>
                        {e.typecls}
                      </span>
                      {e.title && <span> {e.title}</span>}
                    </span>
                  </div>
                ))}
              </>
            )}

            <h4 className="teacher-schedule-section-label">내 일정</h4>
            {activeMine.length === 0 && (
              <div className="empty-row">등록된 개인일정이 없습니다.</div>
            )}
            {activeMine.map((e) => (
              <div key={e.id} className="academic-calendar-entry-row">
                <span>{e.title}</span>
                <span>
                  <button className="btn-link" type="button" onClick={() => startEdit(e)}>수정</button>
                  <button className="btn-link" type="button" onClick={() => handleDelete(e.id)}>삭제</button>
                </span>
              </div>
            ))}

            <div className="academic-calendar-add-row">
              <input
                type="text"
                className="field-input"
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                placeholder={editingId ? '일정 수정' : '새 일정 입력'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSave();
                }}
              />
              <button className="btn-primary btn-inline" type="button" onClick={handleSave}>
                {editingId ? '수정 저장' : '추가'}
              </button>
              {editingId && (
                <button className="btn-ghost" type="button" onClick={resetForm}>취소</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MonthGrid({ year, month, academicByDate, myByDate, selectedDate, onDayClick }) {
  const weeks = useMemo(() => buildWeeks(year, month), [year, month]);

  return (
    <div className="academic-calendar-month">
      <h4>{year}년 {month}월</h4>
      <table className="academic-calendar-table teacher-schedule-table">
        <thead>
          <tr>
            {WEEKDAYS.map((w) => (
              <th key={w}>{w}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((d, di) => {
                if (d === null) return <td key={di} className="academic-calendar-empty" />;
                const dateStr = toDateStr(year, month, d);
                const academicEntries = academicByDate[dateStr] || [];
                const myEntries = myByDate[dateStr] || [];
                return (
                  <td
                    key={di}
                    className={`academic-calendar-day${dateStr === selectedDate ? ' teacher-schedule-selected' : ''}`}
                    onClick={() => onDayClick(dateStr)}>
                    <div className="academic-calendar-daynum">{d}</div>
                    {academicEntries.map((e) => (
                      <div key={`a-${e.id}`} className={`academic-calendar-entry ${typeclsClassName(e.typecls)}`}>
                        {e.typecls}{e.title ? ` - ${e.title}` : ''}
                      </div>
                    ))}
                    {myEntries.map((e) => (
                      <div key={`m-${e.id}`} className="teacher-schedule-entry">
                        {e.title}
                      </div>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function buildWeeks(year, month) {
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
